import { getStepInstruments } from "src/Requests/stepRequests";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { getCCOrderVolumeCalculationOptions } from "src/Utils/ccOrderVolumeCalculationTypes";
// Common Components
import { IEnterPairPositionTableReport } from "src/Interfaces/coveredCall";
import {
  IStepOptionDropdownItem,
  StepOrderSide,
} from "src/Interfaces/stepInterfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CCOrderCriterionType,
  CCOrderSendOrderType,
  ICCOrderAddOrderFormValues,
  ICCOrderCriterionTypes,
  ICCOrderLeg,
  ICCOrderRules,
  IGetCCOrder,
  VolumeCalculationType,
} from "src/Interfaces/ccOrder";
import {
  Button,
  DynamicDropdown,
  Form,
  FullScreenLoading,
  Modal,
  StaticDropdown,
  TextInput,
} from "src/Components";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  addCCOrder,
  getCCOrderCriterionTypes,
  getCCOrderRules,
  getCCOrders,
  getPairTypeDropdown,
} from "src/Requests/ccOrder";
import {
  fallbackCCOrderCriterionTypes,
  getCCOrderCriterionOptions,
  getCCOrderCriterionTypeName,
  getDefaultCCOrderCriterionType,
} from "src/Utils/ccOrderCriterionTypes";
import { toast } from "react-toastify";
import SahraConnections from "src/Pages/Sahra/SahraConnections";
import { deltaHedgeOrderTypesDropdownOptions } from "src/Pages/DeltaHedgeOrder/DeltaHedgeOrder";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";
import PortfoGroupUserMultiSelectDropdown from "src/Components/Dropdown/Users/PortfoGroupUserMultiSelectDropdown";
import OrderPreviewTable, { isCCOrderPreviewRowSendable } from "src/Pages/CCOrder/OrderPreviewTable";
import CriterionTypeDefaultSetting from "src/Pages/CCOrder/CriterionTypeDefaultSetting";
import { CoveredMatrixExecutionMethod } from "src/Interfaces/coveredMatrixOrder";

interface IProps {
  row: IEnterPairPositionTableReport;
  onAction: () => void;
  volumeCalculationType: VolumeCalculationType;
  useFixedBasePrices?: boolean;
}

enum ModalMode {
  Closed,
  SahraOrderTable,
  OrderPreview,
}

const sendOrderTypeOptions = [
  { id: CCOrderSendOrderType.Automatic, name: "\u062E\u0648\u062F\u06A9\u0627\u0631" },
  { id: CCOrderSendOrderType.Credit, name: "\u0627\u0639\u062A\u0628\u0627\u0631\u06CC" },
  { id: CCOrderSendOrderType.NonCredit, name: "\u063A\u06CC\u0631 \u0627\u0639\u062A\u0628\u0627\u0631\u06CC" },
];

const executionMethodOptions = [
  {
    id: CoveredMatrixExecutionMethod.BaseAlreadyOwned,
    name: "موجود بودن نماد پایه",
  },
  {
    id: CoveredMatrixExecutionMethod.FutureBasePurchase,
    name: "قیمت ثابت نماد پایه",
  },
];

const SendCCOrders = ({
  row,
  onAction,
  volumeCalculationType,
  useFixedBasePrices = false,
}: IProps) => {
  const [formValues, setFormValues] = useState({
    coveredCallOrderLegs: [] as ICCOrderLeg[],
    coveredCallOrderSellLeg: {} as ICCOrderLeg,
  } as IGetCCOrder);

  const [instrumentOptions, setInstrumentOptions] = useState(
    [] as IStepOptionDropdownItem[],
  );

  const [modalMode, setModalMode] = useState(ModalMode.Closed);

  const [CCOrders, setCCOrders] = useState<ICCOrderAddOrderFormValues[]>([]);
  const [rules, setRules] = useState({} as ICCOrderRules);
  const [criterionTypes, setCriterionTypes] =
    useState<ICCOrderCriterionTypes>(fallbackCCOrderCriterionTypes);
  const hasUserChangedCriterionType = useRef(false);

  const [loading, setLoading] = useState(false);

  const orderSideDropdown = [
    { id: StepOrderSide.Buy, name: "خرید" },
    { id: StepOrderSide.Sell, name: "فروش" },
  ] as DropdownItem[];

  useEffect(() => {
    getStepInstruments()
      .then((resp) => {
        setInstrumentOptions(resp);
        getInitialFormValues();
      })
      .catch(packetErrorHandler);
    getCCOrderRules().then(setRules).catch(packetErrorHandler);
    getCCOrderCriterionTypes()
      .then(setCriterionTypes)
      .catch(packetErrorHandler);
  }, []);

  const criterionOptions = useMemo(
    () => getCCOrderCriterionOptions(criterionTypes, formValues?.orderType),
    [criterionTypes, formValues?.orderType],
  );

  const getCriterionValue = (criterionType: number | string | null | undefined) =>
    Number(criterionType) === CCOrderCriterionType.MaxRollCost
      ? Number(row.livePairPosition ?? 0)
      : row.gainAfterCommissionDaily
        ? Number(row.gainAfterCommissionDaily.split(" ")[0])
        : 0;

  useEffect(() => {
    if (!formValues?.orderType) return;

    const isAllowedType = criterionOptions.some(
      (item) => item.id?.toString() === formValues?.criterionType?.toString(),
    );

    const defaultCriterionType = getDefaultCCOrderCriterionType(
      criterionTypes,
      formValues.orderType,
    );
    if (!defaultCriterionType) return;
    if (hasUserChangedCriterionType.current && isAllowedType) return;
    if (formValues?.criterionType?.toString() === defaultCriterionType.toString())
      return;

    setFormValues((prev) => ({
      ...prev,
      criterionType: defaultCriterionType,
      criterion: getCriterionValue(defaultCriterionType),
    }));
  }, [criterionOptions, criterionTypes, formValues?.orderType, formValues?.criterionType]);

  function getInitialFormValues() {
    const res = {
      coveredCallOrderLegs: [] as ICCOrderLeg[],
      coveredCallOrderSellLeg: {} as ICCOrderLeg,
      orderType: DeltaHedgeOrderType.Open,
      coveredCallPositionStrategyEnum: row.coveredCallPositionStrategyEnum,
      criterionType: getDefaultCCOrderCriterionType(
        criterionTypes,
        DeltaHedgeOrderType.Open,
      ),
      criterion: getCriterionValue(getDefaultCCOrderCriterionType(
        criterionTypes,
        DeltaHedgeOrderType.Open,
      )),
      involvedFund: row.involvedFundWithContract,
      marginPercent: 0,
      sendOrderType: CCOrderSendOrderType.Automatic,
      executionMethod: useFixedBasePrices
        ? CoveredMatrixExecutionMethod.FutureBasePurchase
        : undefined,
      manualBaseReferencePrice: useFixedBasePrices
        ? row.baseLastPrice
        : undefined,
      baseInstrumentId: useFixedBasePrices
        ? row.baseInstrumentId
        : undefined,
      blockSize: 100,
      portfoPercent: 10,
      volumeCalculationType,
    } as IGetCCOrder;

    res.coveredCallOrderLegs = [
      {
        instrumentId: row.instrumentId1,
        orderSide: StepOrderSide.Buy,
      },
    ];

    res.coveredCallOrderSellLeg = {
      instrumentId: row.instrumentId3,
      orderSide: StepOrderSide.Sell,
    };

    if (row.coveredCallPositionStrategyEnum === 8)
      res.coveredCallOrderLegs.push({
        instrumentId: row.instrumentId2!,
        orderSide: StepOrderSide.Sell,
      });

    setFormValues(res);
  }

  const changeHandler = (name: string, value: any) => {
    if (name === "volumeCalculationType")
      setFormValues({
        ...formValues,
        [name]: value,
        totalVolume: value?.toString() === "1" ? 10 : (undefined as any),
        portfoPercent: value?.toString() !== "1" ? 10 : (undefined as any),
      });
    else if (name === "criterionType") {
      hasUserChangedCriterionType.current = true;
      setFormValues({
        ...formValues,
        criterionType: value,
        criterion: getCriterionValue(value),
      });
    }
    else if (name === "executionMethod") {
      setFormValues({
        ...formValues,
        executionMethod: value,
        manualBaseReferencePrice:
          Number(value) === CoveredMatrixExecutionMethod.FutureBasePurchase
            ? row.baseLastPrice
            : null,
        baseInstrumentId: row.baseInstrumentId,
      });
    }
    else setFormValues({ ...formValues, [name]: value });
  };

  const sendCCOrders = () => {
    if (loading) return;

    setLoading(true);
    const requests = CCOrders.filter(isCCOrderPreviewRowSendable).map((order) =>
      addCCOrder({ ...order, accountIds: [order.accountId] }),
    );
    Promise.all(requests)
      .then(() => {
        toast.success("با موفقیت انجام شد");
        onAction();
        setModalMode(ModalMode.Closed);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const sahraUserTableModalUI = (
    <div className="flex flex-col justify-start items-center gap-3">
      <SahraConnections
        initialSearchParam={{
          userIds: CCOrders.filter(isCCOrderPreviewRowSendable).map((order) => order.accountId),
        }}
      />

      <div className="w-[100px] mr-auto">
        <Button
          text="ثبت"
          variant="contained"
          onClick={sendCCOrders}
          disabled={loading}
          loading={loading}
        />
      </div>
    </div>
  );

  const orderPreviewModalUI = (
    <div className="flex flex-col justify-start items-center gap-3">
      <OrderPreviewTable
        values={CCOrders}
        coveredCallOrderType={DeltaHedgeOrderType.Open}
      />

      <div className="w-[100px] mr-auto">
        <Button
          text="ثبت"
          variant="contained"
          onClick={() => setModalMode(ModalMode.SahraOrderTable)}
          disabled={!CCOrders.some(isCCOrderPreviewRowSendable)}
        />
      </div>
    </div>
  );

  const getModalMode = () => {
    switch (modalMode) {
      case ModalMode.Closed:
        return <></>;

      case ModalMode.SahraOrderTable:
        return sahraUserTableModalUI;

      case ModalMode.OrderPreview:
        return orderPreviewModalUI;

      default:
        return <></>;
    }
  };

  function submitHandler(): void {
    if (loading) return;

    setLoading(true);
    getCCOrders(formValues)
      .then((r) => {
        setCCOrders(
          r.map((order) => ({
            ...order,
            criterionType: order.criterionType ?? formValues.criterionType,
          })),
        );
        setModalMode(ModalMode.OrderPreview);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }
  const doNothing = () => {};

  const volumeCalculationOptions = getCCOrderVolumeCalculationOptions(
    rules.volumeCalculationTypesByProcess?.[
      Number(formValues?.orderType || -1)
    ],
  );
  const volume =
    volumeCalculationOptions.find(
      (item) => item.id === formValues?.volumeCalculationType,
    )?.name ?? "حجم";

  return (
    <Form className="!w-96 gap-3 p-2 px-2 !mx-auto">
      {loading ? <FullScreenLoading /> : <></>}
      {modalMode !== ModalMode.Closed ? (
        <Modal
          title="ارسال سفارش"
          modalBoxClassName="!min-w-0 w-[1320px]"
          onClose={() => setModalMode(ModalMode.Closed)}
          UI={getModalMode()}
        />
      ) : (
        <></>
      )}

      <PortfoGroupUserMultiSelectDropdown
        name="accountIds"
        label="نام اکانت ها"
        value={formValues?.accountIds}
        onChange={changeHandler}
        withTagBox
        extraLabel
      />
      <div
        className={`w-full grid ${formValues?.coveredCallPositionStrategyEnum === 8 ? " grid-cols-3" : "grid-cols-2"} gap-1`}
      >
        <StaticDropdown
          dropdown={{
            name: "orderType",
            placeholder: "فرایند",
            extraLabel: true,
            disabled: true,
          }}
          onChange={changeHandler}
          options={deltaHedgeOrderTypesDropdownOptions}
          value={formValues.orderType}
        />
        <div
          className={`${formValues?.coveredCallPositionStrategyEnum === 8 ? "col-span-2" : ""}`}
        >
          <DynamicDropdown
            dropdown={{
              name: "coveredCallPositionStrategyEnum",
              placeholder: "نوع جفت",
              extraLabel: true,
              disabled: true,
            }}
            value={formValues.coveredCallPositionStrategyEnum}
            getData={getPairTypeDropdown}
            onChange={changeHandler}
          />
        </div>

        <StaticDropdown
          onChange={doNothing}
          dropdown={{
            name: "",
            placeholder: " جهت اول",
            disabled: true,
            extraLabel: true,
          }}
          value={formValues.coveredCallOrderLegs[0]?.orderSide}
          options={orderSideDropdown}
        />
        <StaticDropdown
          onChange={doNothing}
          dropdown={{
            name: "",
            placeholder: " جهت دوم",
            disabled: true,
            extraLabel: true,
          }}
          value={
            formValues?.coveredCallPositionStrategyEnum === 8 // کاورد کال مصنوعی
              ? formValues?.coveredCallOrderLegs[1]?.orderSide
              : formValues?.coveredCallOrderSellLeg?.orderSide
          }
          options={orderSideDropdown}
        />
        {formValues?.coveredCallPositionStrategyEnum === 8 ? ( // کاورد کال مصنوعی
          <StaticDropdown
            dropdown={{
              name: "orderSide",
              placeholder: "جهت 3",
              extraLabel: true,
              disabled: true,
            }}
            onChange={() => {}}
            options={orderSideDropdown}
            value={formValues?.coveredCallOrderSellLeg?.orderSide}
          />
        ) : (
          <></>
        )}

        <StaticDropdown
          value={
            formValues?.coveredCallOrderLegs &&
            formValues?.coveredCallOrderLegs[0]
              ? formValues?.coveredCallOrderLegs[0].instrumentId
              : ""
          }
          onChange={doNothing}
          dropdown={{
            name: "instrumentId",
            placeholder: `نماد اول`,
            extraLabel: true,
            disabled: true,
          }}
          options={instrumentOptions}
        />

        <StaticDropdown
          value={
            formValues?.coveredCallPositionStrategyEnum === 8 // کاورد کال مصنوعی
              ? formValues?.coveredCallOrderLegs[1].instrumentId
              : formValues?.coveredCallOrderSellLeg.instrumentId
          }
          onChange={doNothing}
          dropdown={{
            name: "instrumentId",
            placeholder: `نماد دوم`,
            extraLabel: true,
            disabled: true,
          }}
          options={instrumentOptions}
        />

        {formValues?.coveredCallPositionStrategyEnum === 8 ? ( // کاورد کال مصنوعی
          <StaticDropdown
            value={
              formValues?.coveredCallOrderSellLeg
                ? formValues?.coveredCallOrderSellLeg.instrumentId
                : (null as any)
            }
            onChange={doNothing}
            dropdown={{
              name: "instrumentId",
              placeholder: `نماد سوم`,
              extraLabel: true,
              disabled: true,
            }}
            options={instrumentOptions}
          />
        ) : (
          <></>
        )}
      </div>

      <div className="w-full flex flex-row justify-start gap-1">
        <StaticDropdown
          dropdown={{
            name: "volumeCalculationType",
            placeholder: "روش تعیین حجم",
            extraLabel: true,
          }}
          value={formValues?.volumeCalculationType}
          onChange={changeHandler}
          options={volumeCalculationOptions}
        />
        <TextInput
          label={volume}
          name={
            formValues?.volumeCalculationType?.toString() === "1"
              ? "totalVolume"
              : "portfoPercent"
          }
          onChange={changeHandler}
          value={formValues?.[
            formValues?.volumeCalculationType?.toString() === "1"
              ? "totalVolume"
              : "portfoPercent"
          ]?.toString()}
          extraLabel
        />
      </div>

      <TextInput
        label="سرمایه درگیر"
        value={formValues.involvedFund?.toString()}
        name="involvedFund"
        onChange={changeHandler}
        extraLabel
        kind="priceWithDecimal"
        disabled
      />

      {criterionOptions.length > 1 ? (
        <div className="w-full flex items-end gap-1">
          <div className="flex-1">
            <StaticDropdown
              dropdown={{
                name: "criterionType",
                placeholder: "نوع معیار",
                extraLabel: true,
              }}
              onChange={changeHandler}
              options={criterionOptions}
              value={formValues?.criterionType}
            />
          </div>
          <CriterionTypeDefaultSetting
            orderType={formValues?.orderType}
            criterionTypes={criterionTypes}
            criterionOptions={criterionOptions}
            onSaved={(updatedCriterionTypes, criterionType) => {
              setCriterionTypes(updatedCriterionTypes);
              changeHandler("criterionType", criterionType);
            }}
          />
        </div>
      ) : (
        <></>
      )}
      <TextInput
        label={`معیار: ${getCCOrderCriterionTypeName(criterionTypes, formValues?.orderType, formValues?.criterionType)}`}
        name="criterion"
        onChange={changeHandler}
        value={formValues?.criterion?.toString()}
        classname="ltr text-right"
        extraLabel
      />

      <TextInput
        label="% بلوک"
        value={formValues.blockSize?.toString()}
        name="blockSize"
        onChange={changeHandler}
        extraLabel
      />

      <TextInput
        label="% مارجین"
        value={formValues.marginPercent?.toString()}
        name="marginPercent"
        onChange={changeHandler}
        extraLabel
      />
      {useFixedBasePrices ? (
        <>
          <StaticDropdown
            dropdown={{
              name: "executionMethod",
              placeholder: "روش اجرا",
              extraLabel: true,
            }}
            onChange={changeHandler}
            options={executionMethodOptions}
            value={formValues.executionMethod}
          />
          {formValues.executionMethod ===
          CoveredMatrixExecutionMethod.FutureBasePurchase ? (
            <TextInput
              label="قیمت ثابت نماد پایه"
              name="manualBaseReferencePrice"
              onChange={doNothing}
              value={formValues.manualBaseReferencePrice?.toString() ?? ""}
              classname="ltr text-right"
              extraLabel
              disabled
            />
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
      <StaticDropdown
        dropdown={{
          name: "sendOrderType",
          placeholder: "\u062A\u0631\u062A\u06CC\u0628 \u0627\u0631\u0633\u0627\u0644",
          extraLabel: true,
        }}
        onChange={changeHandler}
        options={sendOrderTypeOptions}
        value={formValues.sendOrderType ?? CCOrderSendOrderType.Automatic}
      />
      <hr />
      <Button
        text="ثبت"
        variant="contained"
        onClick={submitHandler}
        disabled={
          loading ||
          (useFixedBasePrices && !formValues.executionMethod) ||
          !formValues?.volumeCalculationType ||
          !formValues?.accountIds?.length
        }
        loading={loading}
      />
    </Form>
  );
};

export default SendCCOrders;
